import { ChevronDown, X } from 'lucide-react';
import {
  type StackCompound,
  FREQ_LABELS,
  TIER_NAMES,
  COMPOUND_COLORS,
} from '../../data/peptidePresets';
import { calcCompound, fmtMg } from '../../utils/peptideCalculations';
import { useExternalValueSync } from '../../hooks/useExternalValueSync';

interface CompoundCardProps {
  compound: StackCompound;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onUpdate: (field: string, value: string | number) => void;
}

export function CompoundCard({
  compound: c, index, isExpanded, onToggle, onRemove, onUpdate,
}: CompoundCardProps) {
  // Uncontrolled, but still following an externally changed value. The previous
  // key={`conc-${c.conc}`} form remounted the input on every keystroke, which
  // threw the cursor out of the field after a single character.
  const concRef = useExternalValueSync<HTMLInputElement>(c.conc);
  const vialRef = useExternalValueSync<HTMLInputElement>(c.vial);
  const doseRef = useExternalValueSync<HTMLInputElement>(c.dose);

  const color = COMPOUND_COLORS[index % COMPOUND_COLORS.length];
  const r = calcCompound(c);
  const mcg = c.doseUnit === 'mcg' ? c.dose : c.dose * 1000;
  const mg = c.doseUnit === 'mg' ? c.dose : c.dose / 1000;
  const wkD = r.wkMg >= 1 ? `${r.wkMg.toFixed(2)} mg` : `${(r.wkMg * 1000).toFixed(0)} mcg`;
  const tierLabel = TIER_NAMES[c.tier as keyof typeof TIER_NAMES] || 'Custom';

  return (
    <div
      className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-sm mb-3 overflow-hidden animate-fade-in"
      style={{ borderLeftWidth: 3, borderLeftColor: color }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer select-none hover:bg-neutral-50 dark:hover:bg-neutral-700/30 transition-colors gap-2"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 min-w-0 overflow-hidden">
          <span className="text-[15px] font-bold whitespace-nowrap" style={{ color }}>
            {c.name}
          </span>
          <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400">
            {tierLabel}
          </span>
          <span className="hidden sm:flex items-center gap-1">
            <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400">
              {FREQ_LABELS[c.freq] || c.freq}
            </span>
            <span className="font-mono text-[11px] text-neutral-400 dark:text-neutral-500 whitespace-nowrap">
              {r.units.toFixed(1)}u · {r.vol.toFixed(r.vol < 0.01 ? 4 : 3)} mL
            </span>
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            className="px-2 py-1 text-[10px] font-semibold rounded-md bg-rose-500/10 text-rose-500 border border-rose-500/20 hover:bg-rose-500/20 transition-colors"
            onClick={e => { e.stopPropagation(); onRemove(); }}
          >
            <X className="w-3 h-3" />
          </button>
          <ChevronDown
            className={`w-4 h-4 text-neutral-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Body */}
      {isExpanded && (
        <div className="px-4 pb-4">
          {/* Editable fields */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-3">
            <div>
              <label className="block text-[9px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                Conc ({c.concUnit})
              </label>
              <input
                type="number"
                ref={concRef}
                defaultValue={c.conc}
                step="any"
                onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) onUpdate('conc', v); }}
                onBlur={e => { const v = parseFloat(e.target.value); if (isNaN(v)) { onUpdate('conc', 0); e.target.value = '0'; } }}
                className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2 py-1.5 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            <div>
              <label className="block text-[9px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Vial (mL)</label>
              <input
                type="number"
                ref={vialRef}
                defaultValue={c.vial}
                step="any"
                onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) onUpdate('vial', v); }}
                onBlur={e => { const v = parseFloat(e.target.value); if (isNaN(v)) { onUpdate('vial', 0); e.target.value = '0'; } }}
                className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2 py-1.5 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            <div>
              <label className="block text-[9px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Dose</label>
              <input
                type="number"
                ref={doseRef}
                defaultValue={c.dose}
                step="any"
                onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) onUpdate('dose', v); }}
                onBlur={e => { const v = parseFloat(e.target.value); if (isNaN(v)) { onUpdate('dose', 0); e.target.value = '0'; } }}
                className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2 py-1.5 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            <div>
              <label className="block text-[9px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Dose Unit</label>
              <select
                value={c.doseUnit}
                onChange={e => onUpdate('doseUnit', e.target.value)}
                className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2 py-1.5 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
              >
                <option value="mcg">mcg</option>
                <option value="mg">mg</option>
              </select>
            </div>
            <div>
              <label className="block text-[9px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Frequency</label>
              <select
                value={c.freq}
                onChange={e => onUpdate('freq', e.target.value)}
                className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2 py-1.5 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
              >
                {Object.entries(FREQ_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dose Math Results */}
          <div className="bg-neutral-50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
            <div className="px-3 py-1.5">
              <span className="text-[9px] uppercase tracking-widest font-semibold text-neutral-400">Dose Math</span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5">
              <ResultCell value={r.vol.toFixed(r.vol < 0.01 ? 4 : 3)} label="mL to draw" color={color} />
              <ResultCell value={r.units.toFixed(1)} label="Insulin Units" color="#fbbf24" />
              <ResultCell value={`${fmtMg(mg)} mg`} label="mg/dose" color="#a78bfa" />
              <ResultCell value={`${mcg.toFixed(mcg < 10 ? 1 : 0)} mcg`} label="mcg/dose" />
              <ResultCell value={wkD} label="Weekly" color="#22d3ee" />
            </div>
            <div className="px-3 py-1.5 border-t border-neutral-200 dark:border-neutral-700">
              <span className="text-[9px] uppercase tracking-widest font-semibold text-emerald-500">
                Vial — {c.conc} {c.concUnit} × {c.vial} mL
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4">
              <ResultCell value={r.totalMg >= 1 ? r.totalMg.toFixed(1) : r.totalMg.toFixed(3)} label="Vial Total (mg)" color="#34d399" />
              <ResultCell value={r.dpv.toFixed(1)} label="Doses/Vial" color="#22d3ee" />
              <ResultCell value={`${r.vol.toFixed(r.vol < 0.01 ? 4 : 3)} mL`} label="mL/Dose" color="#4f8fff" />
              <ResultCell value={r.wpv.toFixed(1)} label="Wk/Vial" color="#a78bfa" />
            </div>
            <div className="px-3 py-1.5 bg-neutral-100/50 dark:bg-black/15 text-[10px] text-neutral-400 flex justify-between flex-wrap gap-1">
              <span>1 unit on U-100 = 0.01 mL</span>
              <span style={{ color }}>{r.concMg} mg/mL → {(r.concMg * 10).toFixed(1)} mcg/unit</span>
            </div>
          </div>

          {/* Clinical info boxes */}
          {c.tierInfo && (
            <div className="mt-2 rounded-md px-3 py-2 text-[11px] leading-relaxed bg-blue-500/5 border border-blue-500/10 text-blue-600 dark:text-blue-400">
              {c.tierInfo}
            </div>
          )}
          {c.timelineInfo && (
            <div className="mt-1.5 rounded-md px-3 py-2 text-[11px] leading-relaxed bg-cyan-500/5 border border-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-start gap-1.5">
              <span>📅</span>
              <span><strong>Titration:</strong> {c.timelineInfo}</span>
            </div>
          )}
          {c.warnInfo && (
            <div className="mt-1.5 rounded-md px-3 py-2 text-[11px] leading-relaxed bg-rose-500/5 border border-rose-500/10 text-rose-600 dark:text-rose-400">
              {c.warnInfo}
            </div>
          )}
          {c.synergyNote && (
            <div className="mt-1.5 rounded-md px-3 py-2 text-[11px] leading-relaxed bg-emerald-500/5 border border-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-start gap-1.5">
              <span>🔗</span>
              {/* synergyNote contains hardcoded HTML from our own preset data - safe to render */}
              <span>{c.synergyNote.replace(/<[^>]*>/g, '')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultCell({ value, label, color }: { value: string; label: string; color?: string }) {
  return (
    <div className="py-2.5 px-1.5 text-center">
      <div className="font-mono text-base font-semibold" style={color ? { color } : undefined}>{value}</div>
      <div className="text-[9px] text-neutral-400 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}
